import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pin, Loader2, Link2, Calendar, Filter, ChevronRight, History } from "lucide-react";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const SIZE_CLASSES = {
  large:  { dot: "h-4 w-4 ring-[6px]", card: "p-5",  title: "text-lg",  text: "text-sm", color: "bg-[#ff9800] ring-[#ff9800]/30", pill: "bg-[#ff9800]/15 text-[#ff9800]" },
  medium: { dot: "h-3 w-3 ring-4",     card: "p-4",  title: "text-base", text: "text-sm", color: "bg-[#7c4dff] ring-[#7c4dff]/30", pill: "bg-[#7c4dff]/15 text-[#b39bff]" },
  small:  { dot: "h-2.5 w-2.5 ring-2", card: "p-3",  title: "text-sm",  text: "text-xs", color: "bg-zinc-500 ring-zinc-500/30", pill: "bg-zinc-500/15 text-zinc-400" },
};
const MONTHS = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const Historie = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("Alle");
  const [month, setMonth] = useState("Alle");
  const [category, setCategory] = useState("Alle");
  const pd = usePageDescription("historie", { title: "HABBO HISTORIE", intro: "Ereignisse aus 20+ Jahren – von Glitches und Drama bis zu legendaeren Events." });

  useEffect(() => {
    (async () => { try { setItems(await api.listTimeline()); } finally { setLoading(false); } })();
  }, []);

  const years = useMemo(() => Array.from(new Set(items.map((t) => t.year))).sort(), [items]);
  const yearCounts = useMemo(() => {
    const m = {};
    items.forEach((t) => { m[t.year] = (m[t.year] || 0) + 1; });
    return m;
  }, [items]);
  const categories = useMemo(() => Array.from(new Set(items.map((t) => t.category).filter(Boolean))).sort(), [items]);

  const monthsForYear = useMemo(() => {
    if (year === "Alle") return [];
    const set = new Set();
    items.filter((t) => t.year === year).forEach((t) => {
      if (t.date) set.add(parseInt(t.date.slice(5, 7), 10));
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [items, year]);

  const filtered = useMemo(() => items.filter((t) => {
    if (year !== "Alle" && t.year !== year) return false;
    if (month !== "Alle" && t.date) {
      const m = parseInt(t.date.slice(5, 7), 10);
      if (String(m) !== month) return false;
    }
    if (category !== "Alle" && t.category !== category) return false;
    return true;
  }), [items, year, month, category]);

  const stats = useMemo(() => ({
    total: items.length,
    large: items.filter((t) => t.size === "large").length,
    medium: items.filter((t) => t.size === "medium").length,
    small: items.filter((t) => t.size === "small").length,
    years: years.length,
  }), [items, years]);

  // Gruppiert nach Jahr (fuer schoenere Uebersicht)
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((t) => {
      if (!g[t.year]) g[t.year] = [];
      g[t.year].push(t);
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <section className="mx-auto max-w-6xl px-4 py-10" data-testid="historie-page">
      <PageHero eyebrow={pd.eyebrow || "Zeitleiste"} icon={History} title={pd.title} keywords={pd.keywords} intro={pd.intro} />

      {/* Stats-Strip */}
      <div className="mt-6 grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          { label: "Gesamt", value: stats.total, color: "text-white" },
          { label: "Gross", value: stats.large, color: "text-[#ff9800]" },
          { label: "Mittel", value: stats.medium, color: "text-[#7c4dff]" },
          { label: "Klein", value: stats.small, color: "text-zinc-400" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-white/10 bg-[#0f141b] p-3 text-center">
            <div className={`pixel-text text-xl ${s.color}`}>{s.value}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Year-Quick-Jump */}
      <div className="mt-6 rounded-xl border border-white/10 bg-[#0f141b] p-4" data-testid="historie-year-strip">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-400">
          <Calendar className="h-3.5 w-3.5" /> Jahr-Schnellauswahl
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => { setYear("Alle"); setMonth("Alle"); }}
            data-testid="year-all"
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${year === "Alle" ? "border-[#7c4dff] bg-[#7c4dff]/15 text-white" : "border-white/10 bg-[#10141b] text-zinc-300 hover:border-white/25"}`}
          >
            Alle <span className="ml-1 text-zinc-500">({stats.total})</span>
          </button>
          {years.map((y) => (
            <button
              key={y}
              onClick={() => { setYear(y); setMonth("Alle"); }}
              data-testid={`year-${y}`}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${year === y ? "border-[#7c4dff] bg-[#7c4dff]/15 text-white" : "border-white/10 bg-[#10141b] text-zinc-300 hover:border-white/25"}`}
            >
              {y} <span className="ml-1 text-zinc-500">({yearCounts[y]})</span>
            </button>
          ))}
        </div>

        {/* Monat-Strip + Kategorie + Legende */}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/5 pt-3">
          {monthsForYear.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Monat:</span>
              <button onClick={() => setMonth("Alle")} className={`rounded px-2 py-0.5 text-[11px] font-semibold ${month === "Alle" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>Alle</button>
              {monthsForYear.map((m) => (
                <button
                  key={m}
                  onClick={() => setMonth(String(m))}
                  data-testid={`month-${m}`}
                  className={`rounded px-2 py-0.5 text-[11px] font-semibold ${String(m) === month ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`}
                >{MONTHS[m] || m}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Filter className="h-3 w-3 text-zinc-500" />
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded border border-white/10 bg-[#10141b] px-2 py-1 text-[11px] text-zinc-200">
              <option value="Alle">Alle Kategorien</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-zinc-300"><span className="h-3 w-3 rounded-full bg-[#ff9800]" /> Gross</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-zinc-300"><span className="h-2.5 w-2.5 rounded-full bg-[#7c4dff]" /> Mittel</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-zinc-300"><span className="h-2 w-2 rounded-full bg-zinc-400" /> Klein</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-xl border border-white/10 bg-[#0f141b] py-16 text-center text-sm text-zinc-500">
          Keine Eintraege fuer diesen Filter.
        </div>
      ) : (
        <div className="relative mt-10">
          <div className="timeline-line absolute left-4 md:left-[110px] top-0 bottom-0 w-[2px]" />
          <div className="space-y-10">
            {grouped.map(([yr, list]) => (
              <section key={yr} data-testid={`historie-year-section-${yr}`} className="relative">
                {/* Jahr-Marker links */}
                <div className="sticky top-20 z-10 mb-4 inline-flex items-center gap-2">
                  <div className="ml-4 md:ml-0 md:w-[90px] md:text-right">
                    <div className="pixel-text text-2xl text-[#b39bff]">{yr}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{list.length} Eintraege</div>
                  </div>
                  <div className="hidden md:block h-px w-6 bg-white/10" />
                </div>

                <ol className="space-y-3 md:ml-[140px]">
                  {list.map((t, idx) => {
                    const sz = SIZE_CLASSES[t.size] || SIZE_CLASSES.medium;
                    const isAuto = !!t.article_id;
                    return (
                      <li key={t.id || `${yr}-${idx}`} className="relative pl-8 md:pl-6">
                        <div className={`absolute left-0 md:-left-[37px] top-3 rounded-full ${sz.color} ${sz.dot} pulse-dot`} data-testid={`historie-event-${t.id || idx}`} />
                        <div className={`card-hover inline-block w-full rounded-xl border border-white/10 bg-[#0f141b] ${sz.card}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`year-pill rounded px-2 py-0.5 text-[10px] uppercase tracking-wider ${sz.pill}`}>
                              {t.size === "large" ? "Gross" : t.size === "small" ? "Klein" : "Mittel"}
                            </span>
                            {t.date && (
                              <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400">
                                {MONTHS[parseInt(t.date.slice(5, 7), 10)] || ""} {t.date.slice(8, 10)}
                              </span>
                            )}
                            {isAuto ? <Link2 className="h-3.5 w-3.5 text-[#9d7bff]" title="Aus News verknuepft" /> : <Pin className="h-3.5 w-3.5 text-[#ffb84d]" title="Manuell" />}
                            {t.category && <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400">{t.category}</span>}
                          </div>
                          <h3 className={`mt-2 font-semibold text-white ${sz.title}`}>
                            {isAuto ? (
                              <Link to={`/news/${t.article_id}`} className="hover:text-[#b39bff] inline-flex items-center gap-1">
                                {t.title}<ChevronRight className="h-3.5 w-3.5" />
                              </Link>
                            ) : t.title}
                          </h3>
                          {t.text && <p className={`mt-1 text-zinc-400 ${sz.text}`}>{t.text}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default Historie;
